import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  usePatchNegotiationRoundV2Mutation,
  type NegotiationRoundV2Dto,
} from "../../../../../autogen/bff-api";
import { useApiError } from "../../../../../common/errors/useApiError";
import { ISODateSelector } from "../../../../../common/input/DateTimeSelectors/ISODateSelector";
import { TipTap } from "../../../../../common/input/TipTap/TipTap";
import { StepNavigation } from "../../../../../common/StepNavigation";

interface Props {
  round: NegotiationRoundV2Dto;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const RoundDetailsStep = ({ round, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const [patchRound, { isLoading: isPatching }] = usePatchNegotiationRoundV2Mutation();
  const [localTitle, setLocalTitle] = useState(round.title ?? "");

  const patch = async (fields: Parameters<typeof patchRound>[0]["negotiationRoundV2PatchDto"]) => {
    try {
      await patchRound({
        eventId: round.eventId,
        roundId: round.id,
        negotiationRoundV2PatchDto: fields,
      }).unwrap();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <Box pb="8" pt="10">
      <Heading as="h2" size="md" mb="6">
        {t("Details")}
      </Heading>
      <Stack spacing="5" maxW="2xl">
        <FormControl>
          <FormLabel fontSize="sm">{t("Title")}</FormLabel>
          <Input
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            onBlur={() => {
              if (localTitle !== (round.title ?? "")) void patch({ title: localTitle || null });
            }}
            placeholder={t("Negotiation round {{n}}", { n: round.roundNumber }) ?? ""}
            maxW="md"
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">{t("Bid deadline")}</FormLabel>
          <ISODateSelector
            date={round.deadline}
            onChange={(isoDate) => {
              if (isoDate) void patch({ deadline: isoDate });
            }}
          />
        </FormControl>

        <FormControl>
          <HStack>
            <Checkbox
              isChecked={round.isFinalRound ?? false}
              onChange={(e) => void patch({ isFinalRound: e.target.checked })}
              colorScheme="teal"
            >
              <Text fontSize="sm">{t("Final Round")}</Text>
            </Checkbox>
          </HStack>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">{t("Negotiation round description")}</FormLabel>
          <TipTap
            content={round.description ?? ""}
            editable={true}
            onUpdate={(content) => void patch({ description: content })}
          />
        </FormControl>

        {isPatching && <Spinner size="sm" color="smPrimary" />}
      </Stack>
      <StepNavigation onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
